import { useState, useRef } from 'react'
import { Shield, Upload, Eye, EyeOff, AlertTriangle } from 'lucide-react'

interface PkiSignPanelProps {
  onSign: (p12File: File, password: string) => Promise<void>
  country?: string
  disabled?: boolean
}

const COUNTRY_LABELS: Record<string, string> = {
  AR: 'Argentina — Ley 25.506',
  EC: 'Ecuador — Ley de Comercio Electrónico (Ley 67)',
  CL: 'Chile — Ley 19.799',
  CO: 'Colombia — Ley 527',
  MX: 'México — Código de Comercio',
}

export function PkiSignPanel({ onSign, country = 'AR', disabled }: PkiSignPanelProps) {
  const [p12File, setP12File] = useState<File | null>(null)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [signing, setSigning] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const legalLabel = COUNTRY_LABELS[country] ?? country

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null
    setP12File(f)
    setError('')
  }

  const handleSign = async () => {
    if (!p12File) { setError('Selecciona tu certificado .p12'); return }
    if (!password) { setError('Ingresa la contraseña del certificado'); return }
    if (p12File.size > 1024 * 1024) { setError('El certificado no debe superar 1MB'); return }

    setSigning(true)
    setError('')
    try {
      await onSign(p12File, password)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al firmar'
      setError(msg)
    } finally {
      setSigning(false)
      setPassword('')
    }
  }

  return (
    <div className="space-y-4">
      {/* Legal context */}
      <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <Shield size={18} className="text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs font-medium text-blue-800 dark:text-blue-300">
            Firma Digital con Certificado PKI
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
            {legalLabel}. Tu clave privada nunca sale de este formulario — la firma ocurre de forma segura en el servidor y el certificado se descarta inmediatamente.
          </p>
        </div>
      </div>

      {/* Certificate file */}
      <div>
        <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
          Certificado digital (.p12 / .pfx)
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 border border-legal-300 dark:border-legal-600 rounded-lg text-sm text-legal-700 dark:text-legal-200 hover:bg-legal-50 dark:hover:bg-legal-700"
          >
            <Upload size={16} />
            {p12File ? p12File.name : 'Seleccionar archivo'}
          </button>
          {p12File && (
            <span className="text-xs text-legal-500 dark:text-legal-400">
              {(p12File.size / 1024).toFixed(1)} KB
            </span>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".p12,.pfx,application/x-pkcs12"
          onChange={handleFileChange}
          className="hidden"
        />
        <p className="text-xs text-legal-400 dark:text-legal-500 mt-1">
          Emitido por BCE, Security Data, ANF u otra CA acreditada
        </p>
      </div>

      {/* Password */}
      <div>
        <label className="block text-sm font-medium text-legal-700 dark:text-legal-200 mb-1">
          Contraseña del certificado
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSign()}
            autoComplete="current-password"
            placeholder="Contraseña del .p12"
            className="w-full pr-10 px-3 py-2 border border-legal-300 dark:border-legal-600 rounded-lg bg-white dark:bg-legal-900 text-legal-900 dark:text-white text-sm"
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-legal-400 hover:text-legal-600"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <AlertTriangle size={16} className="text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      <button
        onClick={handleSign}
        disabled={!p12File || !password || signing || disabled}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Shield size={16} />
        {signing ? 'Firmando con certificado...' : 'Firmar con Certificado Digital'}
      </button>
    </div>
  )
}
